'use strict';

import ChevronLeft from '@mdi/svg/svg/chevron-left.svg?react';
import ChevronRight from '@mdi/svg/svg/chevron-right.svg?react';
import { Flex, MDIIcon } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import React, { Component } from 'react';
import ComponentUtil from 'utils/ComponentUtil';
import './Scroller.sass';

interface ScrollerProps {
  alwaysShowArrows?: boolean;
  carousel?: boolean;
  initIndex?: number;
  items: React.ReactNode[];
  itemWidth: number;
  width: number;
  disableControl?: boolean;
  camSource?: string;
  actions?: React.ReactNode;
}

interface ScrollerState {
  currIndex: number;
  currXTranslation: number;
  itemsXTranslationArray: number[];
  mouseEntered?: boolean;
}

export default class Scroller extends Component<ScrollerProps, ScrollerState> {
  constructor(props: ScrollerProps) {
    super(props);

    const { width, items, itemWidth, initIndex = 0 } = props;

    this.state = {
      currIndex: initIndex,
      currXTranslation: initIndex * itemWidth,
      itemsXTranslationArray: this._computeXTranslationsPerItem({ width, items, itemWidth }),
    };
  }

  componentDidUpdate(prevProps: ScrollerProps) {
    const { camSource, width, items, itemWidth } = prevProps;
    const {
      camSource: newCamSource,
      width: newWidth,
      items: newItems,
      itemWidth: newItemWidth,
    } = this.props;

    const newState: Partial<ScrollerState> = {};

    // TODO This is kind of weird, it is the only use case
    // specific bit of logic - everything else in this component
    // is agnostic of use case
    if (camSource !== newCamSource) {
      Object.assign(newState, {
        currIndex: 0,
        currXTranslation: 0,
      });
    }

    if (width !== newWidth || itemWidth !== newItemWidth || !isEqual(items, newItems)) {
      newState.itemsXTranslationArray = this._computeXTranslationsPerItem({
        width: newWidth,
        items: newItems,
        itemWidth: newItemWidth,
      });
    }

    // Note: We are setting state here based on past state, can use functional
    // setState if problems arise later on
    this.setState(newState as ScrollerState);
  }

  shouldComponentUpdate(nextProps: ScrollerProps, nextState: ScrollerState) {
    return ComponentUtil.shouldComponentUpdate({
      context: this,
      nextProps,
      nextState,
    });
  }

  render() {
    const { alwaysShowArrows, items, actions, disableControl } = this.props;
    const { currIndex, mouseEntered, itemsXTranslationArray, currXTranslation } = this.state;

    const cameraPanelNavLeftClass = classNames('cam-nav', 'left', {
      needScrolling:
        (alwaysShowArrows || mouseEntered) && itemsXTranslationArray.length > 0 && currIndex > 0,
    });

    const cameraPanelNavRightClass = classNames('cam-nav', 'right', {
      needScrolling:
        (alwaysShowArrows || mouseEntered) &&
        itemsXTranslationArray.length > 0 &&
        currIndex < itemsXTranslationArray.length - 1,
    });

    const cameraPanelStyle = { transform: `translateX(${0 - currXTranslation}px)` };

    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        className="scroller-wrap"
        onMouseEnter={this._handleMouseEnter}
        onMouseLeave={this._handleMouseLeave}
      >
        {!disableControl && (
          <Flex
            justifyContent="center"
            alignItems="center"
            data-testid="cam-nav-left"
            className={cameraPanelNavLeftClass}
            onClick={this._moveLeft}
          >
            <MDIIcon icon={ChevronLeft} size={28} fill="var(--color-white)" />
          </Flex>
        )}

        <Flex className="scroller" style={cameraPanelStyle} alignItems="center">
          {items.map((item, idx) => (
            <div key={idx}>{item}</div>
          ))}
        </Flex>
        {actions}
        {!disableControl && (
          <Flex
            justifyContent="center"
            alignItems="center"
            data-testid="cam-nav-right"
            className={cameraPanelNavRightClass}
            onClick={this._moveRight}
          >
            <MDIIcon icon={ChevronRight} size={28} fill="var(--color-white)" />
          </Flex>
        )}
      </Flex>
    );
  }

  _handleMouseEnter = () => {
    this.setState({ mouseEntered: true });
  };

  _handleMouseLeave = () => {
    this.setState({ mouseEntered: false });
  };

  _moveLeft = () => {
    const { currIndex, currXTranslation, itemsXTranslationArray } = this.state;
    const newCurrIndex = currIndex - 1;
    const newXTranslation = currXTranslation - itemsXTranslationArray[currIndex];
    // Note: We are setting state here based on past state, can use functional
    // setState if problems arise later on
    this.setState({
      currXTranslation: newXTranslation,
      currIndex: newCurrIndex,
    });
  };

  _moveRight = () => {
    const { currIndex, currXTranslation, itemsXTranslationArray } = this.state;
    const newCurrIndex = currIndex + 1;
    const newXTranslation = currXTranslation + itemsXTranslationArray[newCurrIndex];

    // Note: We are setting state here based on past state, can use functional
    // setState if problems arise later on
    this.setState({
      currXTranslation: newXTranslation,
      currIndex: newCurrIndex,
    });
  };

  /**
   * @return {Array} of scroll widths corresponding to each item
   */

  _computeXTranslationsPerItem({
    width,
    items,
    itemWidth,
  }: {
    width: number;
    items: React.ReactNode[];
    itemWidth: number;
  }): number[] {
    const W = width;
    const L = items.length;
    const N = Math.floor(W / itemWidth);

    const array: number[] = [];

    if (L <= N) {
      return array;
    }

    const numOfScrolls = Math.floor(L / N);
    for (let i = 0; i < numOfScrolls; i++) {
      array[i] = N * itemWidth;
    }

    const lastScroll = L % N;
    if (lastScroll > 0) {
      array.push(lastScroll * itemWidth);
    }

    return array;
  }
}
